/**
 * `ShareDecoderFailedScreen` — GVW-169 standalone state page.
 *
 * Share-link decoder failure. Reads ?reason= query param. Alert role + fallback to workspace.
 *
 * Cite: design/30-engineering/ui-implementation/GVW-169/brief.md
 * Copy: error-catalog.md GV-ERR-SHARE-DECODE, copy-deck.md gvw.169.*
 */

import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { tokens } from '@/design';
import { useLiveRegion } from '@/state/liveRegion';

export function ShareDecoderFailedScreen() {
  const liveRegion = useLiveRegion();
  const [searchParams] = useSearchParams();
  
  // Query param per brief.md step 4.f
  const reasonCode = searchParams.get('reason') || 'DECODE_FAILED';

  useEffect(() => {
    // Copy key: gvw.169.title
    document.title = 'Share link could not be opened · GeoViewer';
    // Alert role per error-catalog.md GV-ERR-SHARE-DECODE
    liveRegion.announce('alert', 'Share link could not be opened');
  }, [liveRegion]);

  return (
    <main
      role="main"
      aria-labelledby="share-decoder-failed-heading"
      data-gvw-id="GVW-169"
      data-route="/share-decoder-failed"
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: tokens.color.surface[0],
        color: tokens.color.ink.primary,
        fontFamily: tokens.font.family.ui,
        padding: tokens.space[5],
        boxSizing: 'border-box',
      }}
    >
      <section
        style={{
          maxWidth: tokens.layout.standaloneStateExtraWide,
          width: '100%',
          background: tokens.color.surface[1],
          border: tokens.border.thin,
          borderLeft: `${tokens.space[1]}px solid ${tokens.color.semantic.danger}`,
          borderRadius: tokens.radius.lg,
          padding: `${tokens.space[6]}px ${tokens.space[5]}px`,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: tokens.space[4],
          textAlign: 'center',
        }}
      >
        {/* Hero glyph per brief.md icon plan */}
        <div
          aria-hidden="true"
          style={{
            fontSize: 32,
            color: tokens.color.semantic.danger,
            fontFamily: 'Material Symbols Outlined',
          }}
        >
          link_off
        </div>

        {/* Copy key: gvw.169.title from copy-deck.md */}
        <h1
          id="share-decoder-failed-heading"
          style={{
            margin: 0,
            fontSize: tokens.font.size['2xl'],
            lineHeight: tokens.font.lineHeight.tight,
            fontWeight: tokens.font.weight.semibold,
            color: tokens.color.ink.primary,
          }}
        >
          Share link could not be opened
        </h1>

        {/* Copy key: gvw.169.body */}
        <p
          style={{
            margin: 0,
            fontSize: tokens.font.size.md,
            lineHeight: tokens.font.lineHeight.normal,
            color: tokens.color.ink.secondary,
            maxWidth: '50ch',
          }}
        >
          The link is broken or out of date. Loading your default view.
        </p>

        {/* Recovery action per navigation-map.md §8 */}
        <button
          type="button"
          onClick={() => {
            // Continue to default workspace per brief.md §10
            window.location.href = '/workspace';
          }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: tokens.space[2],
            padding: `${tokens.space[3]}px ${tokens.space[4]}px`,
            background: tokens.color.accent.primary,
            color: tokens.color.accent.on,
            border: 'none',
            borderRadius: tokens.radius.md,
            fontSize: tokens.font.size.md,
            fontWeight: tokens.font.weight.medium,
            fontFamily: 'inherit',
            cursor: 'pointer',
          }}
        >
          Continue
        </button>

        {/* Reason code per brief.md §16 + WIREFRAMES requirement */}
        <div
          style={{
            marginTop: tokens.space[2],
            fontSize: tokens.font.size.sm,
            color: tokens.color.ink.muted,
          }}
        >
          <span>Reason: </span>
          <code
            style={{
              fontFamily: tokens.font.family.mono,
              background: tokens.color.surface[2],
              padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
              borderRadius: tokens.radius.sm,
              userSelect: 'all',
            }}
          >
            {reasonCode}
          </code>
        </div>

        {/* Error severity indicator */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: tokens.space[1],
            fontSize: tokens.font.size.xs,
            color: tokens.color.semantic.danger,
            marginTop: tokens.space[1],
          }}
        >
          <span
            aria-hidden="true"
            style={{
              fontFamily: 'Material Symbols Outlined',
              fontSize: 14,
            }}
          >
            error
          </span>
          Share link decode error
        </div>
      </section>
    </main>
  );
}